import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PostVideoPrivateCommentsResult,
  PostVideoPrivateCommentsOptions,
  GetVideoPrivateCommentsOptions,
  GetVideoPrivateCommentsResult,
  getVideoPrivateComments,
  PostVideoPrivateCommentsFields,
  postVideoPrivateComments,
  GetVideoPrivateCommentsFields,
} from '../fetchers/video/private_comments';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetVideoPrivateCommentsOptions<Fields extends GetVideoPrivateCommentsFields> =
  GetVideoPrivateCommentsOptions<Fields>;

/**
 * Get all the private comments associated with a video
 * This method returns every private comment associated with the specified video.
 */

export function useGetVideoPrivateComments<Fields extends GetVideoPrivateCommentsFields>(
  getOptions:
    | UseGetVideoPrivateCommentsOptions<Fields>
    | (() => UseGetVideoPrivateCommentsOptions<Fields> | null),
  config?: SWRConfiguration<GetVideoPrivateCommentsResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options
      ? `/videos/${options.where.videoId}/private_comments${serializeQuery(options)}`
      : () => null,
    options
      ? () => {
          return getVideoPrivateComments({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoPrivateComments, {
    endpoint: '/videos/:videoId/private_comments',
    method: 'GET',
  });
}

/**
 * Get all the private comments associated with a video
 * This method returns every private comment associated with the specified video.
 */

export function useGetVideoPrivateCommentsLazy<Fields extends GetVideoPrivateCommentsFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetVideoPrivateCommentsResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetVideoPrivateCommentsOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/private_comments${serializeQuery(options)}`,
          getVideoPrivateComments({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoPrivateCommentsLazy, {
    endpoint: '/videos/:videoId/private_comments',
    method: 'GET',
  });
}

type UseGetVideoPrivateCommentsOptionsInfinite<Fields extends GetVideoPrivateCommentsFields> =
  GetVideoPrivateCommentsOptions<Fields>;

/**
 * Get all the private comments associated with a video
 * This method returns every private comment associated with the specified video.
 */

export function useGetVideoPrivateCommentsInfinite<Fields extends GetVideoPrivateCommentsFields>(
  getOptions:
    | UseGetVideoPrivateCommentsOptionsInfinite<Fields>
    | (() => UseGetVideoPrivateCommentsOptionsInfinite<Fields> | null),
  config?: SWRInfiniteConfiguration<GetVideoPrivateCommentsResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let getKey = (
    pageIndex: number,
    previousPageData: GetVideoPrivateCommentsResult<Fields> | null,
  ) => {
    if (options === null) {
      return null;
    }
    if (previousPageData && !previousPageData.paging.next) {
      return null;
    }
    let { perPage = 25, page: _page, ...rest } = options.query ?? {};
    let select = options.select.join(',');
    let queryString = Object.entries(rest ?? {})
      .filter(([, value]) => typeof value !== 'undefined')
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    return [
      `/videos/${options.where.videoId}/private_comments?page=${
        pageIndex + 1
      }&perPage=${perPage}&fields=${select}&${queryString}`,
      pageIndex,
    ];
  };
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWRInfinite(
    getKey,
    options !== null
      ? ([_uri, pageIndex]: [string, number]) => {
          return getVideoPrivateComments({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            query: { ...options.query, page: pageIndex + 1 },
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoPrivateCommentsInfinite, {
    endpoint: '/videos/:videoId/private_comments',
    method: 'GET',
  });
}

type UsePostVideoPrivateCommentsOptions<Fields extends PostVideoPrivateCommentsFields> =
  PostVideoPrivateCommentsOptions<Fields>;

/**
 * Add a private comment to a video
 * This method adds a private comment to the single video page. The comment appears only when the video is accessed through its page on Vimeo.
 */

export function usePostVideoPrivateComments<Fields extends PostVideoPrivateCommentsFields>() {
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PostVideoPrivateCommentsResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePostVideoPrivateCommentsOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await postVideoPrivateComments({
          ...options,
          baseUrl,
          headers: {
            ...options.headers,
            'Content-Type': 'application/json',
            Authorization: jwt ? `jwt ${jwt}` : '',
            'Vimeo-Page': `${xVimeoPage}`,
            'Accept-Language': locale ?? 'en',
          },
        });
        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePostVideoPrivateComments, {
    endpoint: '/videos/:videoId/private_comments',
    method: 'POST',
  });
}
